/**
 * `<AudienceListPicker>` — pick several tags (or newsletters) for a
 * newsletter's audience, moved out of `pages/Newsletter.tsx` on 2026-10-31
 * when `/campaigns` came to pick an email edition's tags the same way
 * (email-newsletter-tag-audience).
 */

import { useMemo } from "react";
import { SearchableSelect, type SearchableSelectOption } from "./SearchableSelect";

/**
 * A list audience's picker: what is picked as chips with a "×", and a
 * searchable dropdown of the rest to add one more — until the list's cap.
 * The WhatsApp newsletter's tags and newsletters (`/newsletter`), and since
 * 2026-10-31 an email edition's tags (`/campaigns`,
 * email-newsletter-tag-audience).
 */
export function AudienceListPicker({
  picked,
  options,
  onChange,
  max,
  noun,
  emptyText,
  loadError,
  note,
}: {
  readonly picked: ReadonlyArray<string>;
  /** Every pickable option; null while they load. */
  readonly options: ReadonlyArray<SearchableSelectOption> | null;
  readonly onChange: (next: ReadonlyArray<string>) => void;
  readonly max: number;
  /** "tag" / "newsletter". */
  readonly noun: string;
  readonly emptyText: string;
  readonly loadError: string | null;
  readonly note: string;
}): JSX.Element {
  const labelOf = (value: string): string =>
    options?.find((o) => o.value === value)?.label ?? value;
  const rest = useMemo(
    () => (options === null ? null : options.filter((o) => !picked.includes(o.value))),
    [options, picked],
  );
  return (
    <div className="newsletter-audience-tag">
      {picked.length > 0 && (
        <div className="newsletter-audience-chips">
          {picked.map((value) => (
            <span key={value} className="member-tag-chip">
              {labelOf(value)}
              <button
                type="button"
                className="member-tag-remove"
                title={`Remove ${labelOf(value)}`}
                aria-label={`Remove ${labelOf(value)}`}
                onClick={() => onChange(picked.filter((v) => v !== value))}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      {picked.length < max ? (
        <SearchableSelect
          options={rest}
          value={null}
          onChange={(value) => onChange([...picked, value])}
          ariaLabel={`Add a ${noun}`}
          placeholder={picked.length === 0 ? `Choose a ${noun}` : `Add another ${noun}`}
          searchPlaceholder={`Search ${noun}s`}
          emptyText={picked.length > 0 && options !== null && options.length > 0 ? `Every ${noun} is picked.` : emptyText}
          noMatchText={(q) => `No ${noun} matches “${q}”.`}
          loadError={loadError}
        />
      ) : (
        <span className="subtle">
          {String(max)} {noun}s is the most one newsletter may name.
        </span>
      )}
      <span className="subtle">{note}</span>
    </div>
  );
}
